import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@/components/base/Loading";
import { formatFullDate } from "@/lib/format";
import { downloadCsv } from "@/lib/csv";
import type { AlertLog, AlertThreshold, CheckResult } from "@/pages/admin/monitor/hooks/useMonitorAlerts";

interface AlertPanelProps {
  thresholds: AlertThreshold[];
  logs: AlertLog[];
  loading: boolean;
  checking: boolean;
  onSave: (rows: { metric: string; threshold: number; enabled: boolean }[]) => Promise<number>;
  onCheck: () => Promise<CheckResult>;
  notify: (text: string, tone?: "success" | "error") => void;
}

const METRIC_LABEL: Record<string, string> = {
  uptime: "平台可用率",
  api_requests: "接口请求量",
};

const METRIC_UNIT: Record<string, string> = {
  uptime: "%",
  api_requests: "次",
};

const METRIC_HINT: Record<string, string> = {
  uptime: "平台可用率低于该值时触发告警",
  api_requests: "单日接口请求量低于该值时触发告警",
};

const levelTone: Record<string, { chip: string; icon: string }> = {
  error: { chip: "bg-accent-100 text-accent-700", icon: "ri-error-warning-line" },
  warning: { chip: "bg-accent-100 text-accent-700", icon: "ri-alert-line" },
  success: { chip: "bg-primary-100 text-primary-700", icon: "ri-checkbox-circle-line" },
  info: { chip: "bg-secondary-100 text-secondary-900", icon: "ri-information-line" },
};

type EventFilter = "all" | "breach" | "recovery";

const EVENT_FILTERS: { key: EventFilter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "breach", label: "仅告警" },
  { key: "recovery", label: "仅恢复" },
];

function monthLabel(key: string): string {
  if (!/^\d{4}-\d{2}$/.test(key)) return "未知月份";
  const [year, month] = key.split("-");
  return `${year} 年 ${Number(month)} 月`;
}

interface DraftRow {
  metric: string;
  label: string;
  threshold: string;
  enabled: boolean;
}

function toDraft(rows: AlertThreshold[]): DraftRow[] {
  return rows.map((row) => ({
    metric: row.metric,
    label: row.label,
    threshold: String(row.threshold),
    enabled: row.enabled,
  }));
}

export default function AlertPanel({
  thresholds,
  logs,
  loading,
  checking,
  onSave,
  onCheck,
  notify,
}: AlertPanelProps) {
  const saved = useMemo(() => toDraft(thresholds), [thresholds]);
  const [draft, setDraft] = useState<DraftRow[]>(saved);
  const [saving, setSaving] = useState(false);
  const [eventFilter, setEventFilter] = useState<EventFilter>("all");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const dirty = useMemo(
    () =>
      draft.some((row, index) => {
        const base = saved[index];
        if (!base) return true;
        return row.threshold !== base.threshold || row.enabled !== base.enabled;
      }),
    [draft, saved],
  );

  const filteredLogs = useMemo(
    () =>
      logs.filter((log) => {
        if (eventFilter === "all") return true;
        const type = log.event_type === "recovery" ? "recovery" : "breach";
        return type === eventFilter;
      }),
    [logs, eventFilter],
  );

  // 按记录所在月份归档，方便按月翻查历史告警与恢复记录。
  const monthGroups = useMemo(() => {
    const map = new Map<string, AlertLog[]>();
    filteredLogs.forEach((log) => {
      const key = (log.created_at ?? "").slice(0, 7) || "未知";
      const list = map.get(key) ?? [];
      list.push(log);
      map.set(key, list);
    });
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([key, items]) => ({
        key,
        label: monthLabel(key),
        items,
        breaches: items.filter((log) => log.event_type !== "recovery").length,
        recoveries: items.filter((log) => log.event_type === "recovery").length,
      }));
  }, [filteredLogs]);

  const updateRow = (metric: string, patch: Partial<DraftRow>) => {
    setDraft((prev) => prev.map((row) => (row.metric === metric ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    const rows = draft.map((row) => ({
      metric: row.metric,
      threshold: Number(row.threshold),
      enabled: row.enabled,
    }));
    const invalid = rows.some((row) => !Number.isFinite(row.threshold) || row.threshold < 0);
    if (invalid) {
      notify("阈值必须是大于等于 0 的数字", "error");
      return;
    }
    setSaving(true);
    try {
      const updated = await onSave(rows);
      notify(`已保存 ${updated} 项告警阈值`, "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : "保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCheck = async () => {
    try {
      const result = await onCheck();
      const parts: string[] = [];
      if (result.created > 0) parts.push(`新增 ${result.created} 条告警`);
      if (result.recovered > 0) parts.push(`${result.recovered} 项指标恢复`);
      notify(parts.length > 0 ? `检测完成，${parts.join("，")}` : "检测完成，当前未触发新告警或恢复", "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : "检测失败", "error");
    }
  };

  const handleExport = () => {
    if (filteredLogs.length === 0) {
      notify("当前筛选下暂无可导出的告警日志", "error");
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    const scopeLabel = eventFilter === "breach" ? "仅告警" : eventFilter === "recovery" ? "仅恢复" : "全部";
    downloadCsv(
      `监控告警日志_${scopeLabel}_${stamp}.csv`,
      [
        { key: "time", label: "记录时间", value: (row) => formatFullDate(row.created_at) },
        { key: "event", label: "事件类型", value: (row) => (row.event_type === "recovery" ? "恢复" : "告警") },
        { key: "metric", label: "指标", value: (row) => METRIC_LABEL[row.metric] ?? row.metric },
        { key: "date", label: "数据日期", value: (row) => row.metric_date },
        { key: "threshold", label: "阈值", value: (row) => row.threshold },
        { key: "actual", label: "实际值", value: (row) => row.actual_value },
        { key: "level", label: "级别", value: (row) => row.level },
        { key: "message", label: "描述", value: (row) => row.message },
      ],
      filteredLogs,
    );
    notify(`已导出 ${filteredLogs.length} 条告警日志`, "success");
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-alarm-warning-line text-lg" />
          </span>
          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">阈值预警</h3>
            <p className="mt-1 text-xs text-foreground-500">
              可用率或请求量跌破设定值时，自动写入告警日志并向超管、客服推送站内通知；指标回升后补一条恢复提醒。
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || filteredLogs.length === 0}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-download-2-line" />
            </span>
            导出告警日志
          </button>
          <button
            type="button"
            onClick={handleCheck}
            disabled={checking || loading}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={`flex h-4 w-4 items-center justify-center text-primary-700 ${checking ? "animate-spin" : ""}`}>
              <i className={checking ? "ri-loader-4-line" : "ri-radar-line"} />
            </span>
            {checking ? "检测中…" : "立即检测"}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving || loading}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-save-line" />
            </span>
            {saving ? "保存中…" : dirty ? "保存阈值" : "已保存"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 space-y-3">
          {[0, 1].map((item) => (
            <Skeleton key={item} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {draft.map((row) => (
              <div key={row.metric} className="rounded-md border border-background-200 bg-background-100 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{row.label}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">
                      {METRIC_HINT[row.metric] ?? "低于该值时触发告警"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => updateRow(row.metric, { enabled: !row.enabled })}
                    className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      row.enabled
                        ? "bg-primary-100 text-primary-700"
                        : "bg-background-200 text-foreground-600"
                    }`}
                    aria-pressed={row.enabled}
                  >
                    <span className="flex h-3 w-3 items-center justify-center">
                      <i className={row.enabled ? "ri-toggle-fill" : "ri-toggle-line"} />
                    </span>
                    {row.enabled ? "已启用" : "已停用"}
                  </button>
                </div>
                <label className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-foreground-600">告警阈值</span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={row.threshold}
                    onChange={(event) => updateRow(row.metric, { threshold: event.target.value })}
                    className="w-28 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                  <span className="text-xs text-foreground-500">{METRIC_UNIT[row.metric] ?? ""}</span>
                </label>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h4 className="text-sm font-medium text-foreground-950">告警日志</h4>
              <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
                {EVENT_FILTERS.map((item) => {
                  const active = item.key === eventFilter;
                  const count =
                    item.key === "all"
                      ? logs.length
                      : logs.filter((log) =>
                          item.key === "recovery" ? log.event_type === "recovery" : log.event_type !== "recovery",
                        ).length;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setEventFilter(item.key)}
                      className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                        active ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {item.label}
                      <span className="text-foreground-400">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {filteredLogs.length === 0 ? (
              <div className="mt-3 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 py-10 text-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                  <i className="ri-shield-check-line text-lg" />
                </span>
                <p className="text-sm text-foreground-600">
                  {eventFilter === "all" ? "暂无告警记录，各项指标运行正常" : "当前筛选下暂无对应记录"}
                </p>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {monthGroups.map((group) => {
                  const isCollapsed = collapsed[group.key] === true;
                  return (
                    <div key={group.key} className="overflow-hidden rounded-lg border border-background-200">
                      <button
                        type="button"
                        onClick={() => setCollapsed((prev) => ({ ...prev, [group.key]: !isCollapsed }))}
                        className="flex w-full cursor-pointer items-center justify-between gap-3 bg-background-100 px-4 py-2.5 text-left transition-colors hover:bg-background-200/60"
                      >
                        <span className="flex items-center gap-2">
                          <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                            <i className={isCollapsed ? "ri-arrow-right-s-line" : "ri-arrow-down-s-line"} />
                          </span>
                          <span className="text-sm font-medium text-foreground-950">{group.label}</span>
                          <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-600">
                            共 {group.items.length} 条
                          </span>
                        </span>
                        <span className="flex items-center gap-2 text-[11px]">
                          <span className="rounded-full bg-accent-100 px-2 py-0.5 text-accent-700">
                            告警 {group.breaches}
                          </span>
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-primary-700">
                            恢复 {group.recoveries}
                          </span>
                        </span>
                      </button>

                      {!isCollapsed && (
                        <ul className="space-y-2 p-3">
                          {group.items.map((log) => {
                            const tone = levelTone[log.level] ?? levelTone.warning;
                            const isRecovery = log.event_type === "recovery";
                            return (
                              <li
                                key={log.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-3"
                              >
                                <div className="flex items-center gap-3">
                                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${tone.chip}`}>
                                    <i className={`${tone.icon} text-base`} />
                                  </span>
                                  <div>
                                    <p className="text-sm text-foreground-900">{log.message}</p>
                                    <p className="mt-0.5 text-xs text-foreground-500">
                                      {METRIC_LABEL[log.metric] ?? log.metric} · 数据日期 {log.metric_date} · 记录于{" "}
                                      {formatFullDate(log.created_at)}
                                    </p>
                                  </div>
                                </div>
                                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tone.chip}`}>
                                  {isRecovery ? "已恢复" : "告警"}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}